import { useEffect, useId, useState } from "react";
import { cn } from "../../lib/utils";

function getCenterPoint(rect, containerRect) {
  return {
    x: rect.left + rect.width / 2 - containerRect.left,
    y: rect.top + rect.height / 2 - containerRect.top
  };
}

export function AnimatedBeam({
  containerRef,
  fromRef,
  toRef,
  className,
  curvature = 0,
  duration = 3.2,
  delay = 0,
  reverse = false,
  pathColor = "rgba(100, 116, 139, 0.35)",
  gradientStartColor = "#22d3ee",
  gradientStopColor = "#818cf8",
  strokeWidth = 2
}) {
  const beamId = useId().replace(/:/g, "");
  const [path, setPath] = useState("");
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const containerEl = containerRef?.current;
    const fromEl = fromRef?.current;
    const toEl = toRef?.current;
    if (!containerEl || !fromEl || !toEl) return undefined;

    const updatePath = () => {
      const containerRect = containerEl.getBoundingClientRect();
      const fromRect = fromEl.getBoundingClientRect();
      const toRect = toEl.getBoundingClientRect();

      const start = getCenterPoint(fromRect, containerRect);
      const end = getCenterPoint(toRect, containerRect);
      const control = {
        x: (start.x + end.x) / 2,
        y: (start.y + end.y) / 2 - curvature
      };

      setSize({ width: containerRect.width, height: containerRect.height });
      setPath(`M ${start.x},${start.y} Q ${control.x},${control.y} ${end.x},${end.y}`);
    };

    updatePath();

    const observer = new ResizeObserver(updatePath);
    observer.observe(containerEl);
    observer.observe(fromEl);
    observer.observe(toEl);
    window.addEventListener("resize", updatePath);
    window.addEventListener("scroll", updatePath, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePath);
      window.removeEventListener("scroll", updatePath, true);
    };
  }, [containerRef, fromRef, toRef, curvature]);

  if (!path || !size.width || !size.height) return null;

  return (
    <svg
      width={size.width}
      height={size.height}
      viewBox={`0 0 ${size.width} ${size.height}`}
      className={cn("pointer-events-none absolute inset-0 z-10", className)}
      aria-hidden
    >
      <defs>
        <linearGradient id={`beam-gradient-${beamId}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={gradientStartColor} stopOpacity="0" />
          <stop offset="45%" stopColor={gradientStartColor} stopOpacity="0.95" />
          <stop offset="100%" stopColor={gradientStopColor} stopOpacity="0" />
        </linearGradient>
      </defs>

      <path d={path} stroke={pathColor} strokeWidth={strokeWidth} fill="none" strokeLinecap="round" />
      <path
        d={path}
        stroke={`url(#beam-gradient-${beamId})`}
        strokeWidth={strokeWidth + 0.6}
        fill="none"
        strokeLinecap="round"
        className={cn("animated-beam-path", reverse && "[animation-direction:reverse]")}
        style={{
          "--beam-duration": `${duration}s`,
          "--beam-delay": `${delay}s`
        }}
      />
    </svg>
  );
}
